import {inject} from '@angular/core';
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  finalize,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  withLatestFrom
} from 'rxjs';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {concatLatestFrom} from '@ngrx/operators';
import {AccountsHttpService} from "../services/accounts-http.service";
import {AccountsActions, AccountsAPiActions, AccountsChannelActions} from "./accounts.actions";
import {AccountsChanelService} from "../services/accounts-chanel.service";
import {AccountModel} from "../models/account.model";
import {CommonsFacadeService} from "../../../commons/angular/state/commons/commons-facade.service";
import {select, Store} from "@ngrx/store";
import {accountsFeature, AccountState} from "./accounts.state";

export const getAccounts = createEffect(
  (actions$ = inject(Actions),
   accountsService = inject(AccountsHttpService),
   accountsStore = inject(Store<AccountModel>),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(AccountsAPiActions.getAccounts),
      concatMap(payload =>
        of(payload).pipe(
          withLatestFrom(accountsStore.select(accountsFeature.selectCursor()))
        )
      ),
      mergeMap(([payload, cursor]) => {
        commonsFacadeService.setLoaderOn();
        let paramsCopy = {...payload.params}
        if (!!cursor) {
          paramsCopy.cursor = cursor;
        }
        return accountsService.getAll(paramsCopy).pipe(
          map((res: {
            cursor: string,
            list: AccountModel[]
          }) => AccountsAPiActions.getAccountsSuccess({accounts: res.list, cursor: res.cursor})),
          catchError((error: { message: string }) => of(AccountsAPiActions.getAccountsFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const getAccount = createEffect(
  (actions$ = inject(Actions),
   accountsService = inject(AccountsHttpService),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(AccountsAPiActions.getAccount),
      mergeMap(payload => {
        commonsFacadeService.setLoaderOn();
        return accountsService.getOne(payload.id).pipe(
          map((account) => AccountsAPiActions.getAccountSuccess({account: account})),
          catchError((error: { message: string }) => of(AccountsAPiActions.getAccountFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const updateAccount = createEffect(
  (actions$ = inject(Actions),
   accountsService = inject(AccountsHttpService),
   commonsFacadeService = inject(CommonsFacadeService)) => {
    return actions$.pipe(
      ofType(AccountsAPiActions.updateAccount),
      switchMap(payload => {
        commonsFacadeService.setLoaderOn();
        return accountsService.updateOne(payload.id, payload.account).pipe(
          map((account) => {
            return AccountsAPiActions.updateAccountSuccess({account: account})
          }),
          catchError((error: { message: string }) => of(AccountsAPiActions.updateAccountFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);

export const deleteAccount = createEffect(
  (actions$ = inject(Actions), accountsService = inject(AccountsHttpService),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(AccountsAPiActions.deleteAccount),
      mergeMap(payload => {
        commonsFacadeService.setLoaderOn();
        return accountsService.deleteOne(payload.id, payload.shouldMask).pipe(
          map((account) => AccountsAPiActions.deleteAccountSuccess({account: account})),
          catchError((error: { message: string }) => of(AccountsAPiActions.deleteAccountFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff()
          })
        )
      })
    );
  },
  {functional: true}
);


export const subscribeAccountsChannels = createEffect(
  (actions$ = inject(Actions),
   accountsChannelService = inject(AccountsChanelService),
   accountsStore = inject(Store<AccountModel>)) => {
    return actions$.pipe(
      ofType(AccountsActions.setAccounts, AccountsAPiActions.getAccountsSuccess, AccountsAPiActions.getAccountSuccess),
      mergeMap(payload => accountsStore.select(accountsFeature.selectAll).pipe(take(1))),
      map((accounts: AccountModel[]) => {
        const accountIds = accounts.map(t => t.id);
        accountIds.forEach(accountId => accountsChannelService.subscribeById(accountId));
      })
    );
  },
  {functional: true, dispatch: false}
);

export const subscribeAllAccountsChannel = createEffect(
  (actions$ = inject(Actions),
   accountsChannelService = inject(AccountsChanelService)) => {
    return actions$.pipe(
      ofType(AccountsChannelActions.subscribe),
      mergeMap(payload =>
        accountsChannelService.getChannelsValues().pipe(
          map((notification) => {
            return AccountsChannelActions.notificationReceived({notification: notification})
          })
        )
      )
    );
  },
  {functional: true}
);

export const unsubscribeAllAccountChannels = createEffect(
  (actions$ = inject(Actions),
   accountsChannelService = inject(AccountsChanelService)) => {
    return actions$.pipe(
      ofType(AccountsChannelActions.unsubscribe, AccountsActions.removeAll, AccountsActions.clearStore),
      map(payload => {
          accountsChannelService.unSubscribeAll();
          return AccountsChannelActions.unsubscribeSuccess();
        }
      )
    );
  },
  {functional: true}
);

export const unsubscribeAccountChannels = createEffect(
  (actions$ = inject(Actions),
   accountsChannelService = inject(AccountsChanelService)) => {
    return actions$.pipe(
      ofType(AccountsActions.removeAccounts),
      map(payload => {
          payload.ids?.forEach(accountId => accountsChannelService.unSubscribeById(accountId));
          return AccountsChannelActions.unsubscribeSuccess();
        }
      )
    );
  },
  {functional: true}
);
